import React from 'react';
import PropTypes from 'prop-types';
import InventoryFieldsRowForCardTypeBoth from './InventoryFieldsRowForCardTypeBoth';

/**
 * InventorySectionForCardTypeBoth component - Section for Cash/Product/Physical reward types
 * Contains inventory fields (quota, leftover inventory, limit per store)
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing inventory fields
 * @param {Function} props.handleFormChange - Function to handle form field changes
 * @param {Function} props.handleValidation - Function to handle validation state
 * @param {Function} props.handleQuotaDigitalCardChange - Function to handle quota digital card field changes
 * @param {Function} props.handleLeftOverInventoryDigitalCardChange - Function to handle leftover inventory digital card field changes
 * @param {Function} props.handleLimitPerStoreDigitalCardChange - Function to handle limit per store digital card field changes
 * @param {Function} props.handleQuotaPhysicalCardChange - Function to handle quota physical card field changes
 * @param {Function} props.handleLeftOverInventoryPhysicalCardChange - Function to handle leftover inventory physical card field changes
 * @param {Function} props.handleLimitPerStorePhysicalCardChange - Function to handle limit per store physical card field changes
 * @param {boolean} props.isQuotaDigitalCardDisabled - Whether quota digital card field should be disabled
 * @param {boolean} props.isQuotaPhysicalCardDisabled - Whether quota physical card field should be disabled
 * @param {string} props.quotaDigitalCardValidationError - Validation error message for quota digital card field
 * @param {string} props.quotaPhysicalCardValidationError - Validation error message for quota physical card field
 * @param {string} props.limitPerStoreDigitalCardValidationError - Validation error message for limit per store digital card field
 * @param {string} props.limitPerStorePhysicalCardValidationError - Validation error message for limit per store physical card field
 * @returns {JSX.Element} The rendered InventorySectionForCardTypeBoth component
 */
const InventorySectionForCardTypeBoth = ({
  formData,
  handleFormChange,
  handleValidation,
  handleQuotaDigitalCardChange,
  handleLeftOverInventoryDigitalCardChange,
  handleLimitPerStoreDigitalCardChange,
  handleQuotaPhysicalCardChange,
  handleLeftOverInventoryPhysicalCardChange,
  handleLimitPerStorePhysicalCardChange,
  isQuotaDigitalCardDisabled,
  isQuotaPhysicalCardDisabled,
  quotaDigitalCardValidationError,
  quotaPhysicalCardValidationError,
  limitPerStoreDigitalCardValidationError,
  limitPerStorePhysicalCardValidationError
}) => {
  return (
    <InventoryFieldsRowForCardTypeBoth
      formData={formData}
      handleFormChange={handleFormChange}
      handleValidation={handleValidation}
      handleQuotaDigitalCardChange={handleQuotaDigitalCardChange}
      handleLeftOverInventoryDigitalCardChange={
        handleLeftOverInventoryDigitalCardChange
      }
      handleLimitPerStoreDigitalCardChange={
        handleLimitPerStoreDigitalCardChange
      }
      handleQuotaPhysicalCardChange={handleQuotaPhysicalCardChange}
      handleLeftOverInventoryPhysicalCardChange={
        handleLeftOverInventoryPhysicalCardChange
      }
      handleLimitPerStorePhysicalCardChange={
        handleLimitPerStorePhysicalCardChange
      }
      isQuotaDigitalCardDisabled={isQuotaDigitalCardDisabled}
      isQuotaPhysicalCardDisabled={isQuotaPhysicalCardDisabled}
      quotaDigitalCardValidationError={quotaDigitalCardValidationError}
      quotaPhysicalCardValidationError={quotaPhysicalCardValidationError}
      limitPerStoreDigitalCardValidationError={
        limitPerStoreDigitalCardValidationError
      }
      limitPerStorePhysicalCardValidationError={
        limitPerStorePhysicalCardValidationError
      }
    />
  );
};

InventorySectionForCardTypeBoth.propTypes = {
  formData: PropTypes.shape({
    quotaDigitalCard: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leftOverInventoryDigitalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    limitPerStoreDigitalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    quotaPhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    leftOverInventoryPhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    limitPerStorePhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ])
  }).isRequired,
  handleFormChange: PropTypes.func.isRequired,
  handleValidation: PropTypes.func.isRequired,
  handleQuotaDigitalCardChange: PropTypes.func.isRequired,
  handleLeftOverInventoryDigitalCardChange: PropTypes.func.isRequired,
  handleLimitPerStoreDigitalCardChange: PropTypes.func.isRequired,
  handleQuotaPhysicalCardChange: PropTypes.func.isRequired,
  handleLeftOverInventoryPhysicalCardChange: PropTypes.func.isRequired,
  handleLimitPerStorePhysicalCardChange: PropTypes.func.isRequired,
  isQuotaDigitalCardDisabled: PropTypes.bool,
  isQuotaPhysicalCardDisabled: PropTypes.bool,
  quotaDigitalCardValidationError: PropTypes.string,
  quotaPhysicalCardValidationError: PropTypes.string,
  limitPerStoreDigitalCardValidationError: PropTypes.string,
  limitPerStorePhysicalCardValidationError: PropTypes.string
};

export default InventorySectionForCardTypeBoth;
